import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { postsService } from '../services/postsService';
import { Post } from '../types';
import PostDetailModal from './PostDetailModal'; // Reusing the modal's content/logic
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';

const PostDetailView: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { select } = useLanguage();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!postId) {
      setError(select({ es: 'ID de publicación no proporcionado.', en: 'Post ID not provided.' }));
      setLoading(false);
      return;
    }

    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedPost = await postsService.getPostById(postId, { bypassModeration: true });
        setPost(fetchedPost);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(select({ es: 'No se pudo cargar la publicación.', en: 'Failed to load post.' }));
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId, select]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="animate-pulse space-y-6">
          <div className="h-64 bg-gray-200 rounded-xl"></div>
          <div className="h-32 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h2 className="text-xl font-bold text-red-900 mb-2">
            {select({ es: 'Error', en: 'Error' })}
          </h2>
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <ArrowLeft size={20} className="inline-block mr-2" />
            {select({ es: 'Volver', en: 'Go back' })}
          </button>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {select({ es: 'Publicación no encontrada', en: 'Post not found' })}
          </h3>
          <p className="text-gray-500">
            {select({ es: 'La publicación que buscas no existe o no está disponible.', en: 'The post you are looking for does not exist or is unavailable.' })}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <ArrowLeft size={20} className="inline-block mr-2" />
            {select({ es: 'Volver', en: 'Go back' })}
          </button>
        </div>
      </div>
    );
  }

  // PostDetailModal expects onClose, onDelete, onLike, onSave props
  // For a standalone view, onClose would navigate back, onDelete would navigate away
  // onLike/onSave would be handled by the PostDetailModal's internal logic or passed through
  return (
    <div className="max-w-4xl mx-auto p-4">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
      >
        <ArrowLeft size={20} />
        {select({ es: 'Volver', en: 'Back' })}
      </button>
      <PostDetailModal
        post={post}
        isOpen={true} // Always open for a view
        onClose={() => navigate(-1)} // Navigate back when "closing" the view
        onDelete={() => navigate('/home')} // Navigate to home after deletion
        onLike={() => { /* handle like if needed, PostDetailModal might handle internally */ }}
        onSave={() => { /* handle save if needed, PostDetailModal might handle internally */ }}
      />
    </div>
  );
};

export default PostDetailView;
